import { pyramidSettings } from 'routes/Explore/components/Settings/components/ChartSettingsDrawer/helpers/settingsByViz/pyramid';
import { getValidSettings } from './helpers';
import { ChartSettingsT, OrderedColumnsT } from 'business/definitions';
import { HorizontalBarChartOption } from './HorizontalBarChartOption';
import { Dataset, SeriePointT } from '../../types';
import { EChartsSerie, SerieLabel } from './types';
import { getMappedColor } from '../helpers/getColor';
import { translateTreeLabel } from 'helpers/chartHelper';
import { formatDisplay } from 'helpers/formatHelper';
import { EChartsOption } from 'echarts';

type AgePyramidData = {
    agent: number;
    'agent.sex': 'F' | 'M';
    'agent.age': number;
};

export class PyramidChartOption extends HorizontalBarChartOption {
    constructor(
        orderedColumns: OrderedColumnsT,
        settings: ChartSettingsT,
        data: ResponseDataT[],
        width: number,
        height: number,
        dataZoomInterval: [number, number] | undefined,
        isPdfModeActivated: boolean,
        isPngModeActivated: boolean,
        isChartViewedOnDashboard: boolean,
    ) {
        // Delete unhandled settings:
        const properSettings = getValidSettings(settings, pyramidSettings);
        super(
            orderedColumns,
            properSettings,
            data,
            width,
            height,
            dataZoomInterval,
            isPdfModeActivated,
            isPngModeActivated,
            isChartViewedOnDashboard,
        );
    }

    /**
     * For the pyramid to work, and show bar on both side,
     * we need to make one of the serie (F here) negative.
     */
    protected override getDataset(): Dataset[] {
        const dataset = super.getDataset();
        return dataset.map((ds) => {
            if (ds.id === 'F') {
                return {
                    ...ds,
                    source: ds.source.map((data: AgePyramidData) => ({
                        ...data,
                        agent: -data['agent'],
                    })),
                };
            }
            return ds;
        });
    }

    /**
     * Pyramid chart use only one type of style:
     * Value inside the bar, with white text and serie's colors border.
     */
    protected override getLabel(
        serie: EChartsSerie,
        serieIndex: number,
    ): SerieLabel {
        const label = {
            ...super.getLabel(serie, serieIndex),
            position: '',
            align: 'center',
            color: 'white',
            textBorderColor: getMappedColor(
                serie.name,
                serieIndex,
                this.getCustomColor(serie.name),
                this.getColor(),
                this.settings.useGradientColor,
            ),
            textBorderWidth: 4,
        };

        // @ts-expect-error [typing]
        return label;
    }

    /**
     * For the bar to show next to each other on the x axis
     * without gap, we need to set the barGap to -1.
     */
    protected override getSeries(): EChartsSerie[] {
        const series = super.getSeries();
        return series.map((s) => ({
            ...s,
            barGap: -1,
        }));
    }

    /**
     * Override the value formatter to always show the absolute value,
     * so that the serie we made negative still shows up as positive.
     */
    protected override getFormattedSerieValue(
        dataPoint: SeriePointT,
        _showEvolution: boolean,
    ) {
        const serieValueRaw = dataPoint.value[
            this.getDatasetValueKey(dataPoint)
        ] as number | string;

        const serieValue = Math.abs(
            typeof serieValueRaw === 'string'
                ? parseFloat(serieValueRaw)
                : serieValueRaw,
        );
        const formattedValue = formatDisplay(
            serieValue,
            this.orderedColumns.y.type,
            this.getDecimalPrecision(),
            this.settings.displayBy,
        );

        return {
            label:
                translateTreeLabel(
                    dataPoint.seriesName,
                    this.treeValueTranslations,
                ) || dataPoint.seriesName,
            marker: dataPoint.marker,
            value: formattedValue,
        };
    }

    /**
     * Just like {@see getFormattedSerieValue}, we show the absolute value.
     */
    protected override getXAxis(): EChartsOption['xAxis'] {
        const xAxis = super.getXAxis();

        return {
            ...xAxis,
            axisLabel: {
                ...xAxis.axisLabel,
                formatter: (value: number) =>
                    formatDisplay(
                        Math.abs(value),
                        this.orderedColumns.y.type,
                        this.getDecimalPrecision(),
                        this.settings.displayBy,
                    ),
            },
        };
    }
}
